export enum FileType {
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ANY_IMAGE = 'image/*',
}

export interface TUploadFile {
  id: string;
  file: File; // Original File object
  url?: string; // URL for preview and final uploaded file
  progress?: number; // Upload progress
  uploading?: boolean; // Upload status
  error?: string; // Error message if any
}

export interface TInitialFile {
  url: string;
  id?: string;
  name?: string;
}

/**
 * Represents the response returned after uploading files.
 *
 * @property uploadedFiles - An array of uploaded file objects, each containing a `url` and an optional `id`.
 * @property additionalDetails - Optional additional details related to the upload, represented as a key-value map.
 */
export interface TUploadFileResponse {
  uploadedFiles: { url: string; id?: string }[];
  additionalDetails?: Record<string, any>;
}

export interface TFileUploaderProps {
  multiple?: boolean;
  maxFileCount?: number;
  maxFileSize?: number;
  onUpload?: (
    files: File[],
    onProgress: (progress: number, fileId: string) => void,
  ) => Promise<TUploadFileResponse>;
  onDelete?: (fileIsRemoved: TUploadFile, uploadedFiles: TUploadFile[]) => Promise<any>;
  onBeforeUpload?: (files: File[]) => boolean | Promise<boolean>;
  initialFiles?: TInitialFile[];
  hideUploaderAfterUpload?: boolean;
  hideUploaderOnInitialFiles?: boolean;
  onUploadSuccess?: (data: {
    uploadedFiles: TUploadFile[];
    additionalDetails?: Record<string, any>;
  }) => void;
  onUploadError?: (error: string) => void;
  onFileRemove?: (
    fileIsRemoved: TUploadFile,
    uploadedFiles: TUploadFile[],
    extraData?: any,
  ) => void;
  onFileRemoveError?: (error: string) => void;
  CustomPreview?: React.ComponentType<TFilePreviewProps>;
  CustomProgressBar?: React.ComponentType<TProgressBarProps>;
  acceptedFileTypes?: FileType[];
  onLimitReached?: (reachedLimit: {
    isMaxFileCountReached: boolean;
    isMaxFileSizeReached: boolean;
    uploadedFiles: TUploadFile[];
    attemptedFiles: File[];
    limits: {
      maxFileCount?: number;
      maxFileSize?: number;
    };
  }) => void;
}

export interface TFilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  fileUrl?: string;
  isPDF?: boolean;
}

export interface TFilePreviewProps {
  file: TUploadFile;
  onRemove: () => void;
  CustomProgressBar?: React.ComponentType<TProgressBarProps>;
}

export interface TProgressBarProps {
  progress: number;
  isUploaded: boolean;
}
